/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */

import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { RoleModel } from '@app/models/role.model';

export class OrganisationCognitoUser {
  private _userId?: string;
  private _organisationId?: string;
  private _username?: string;
  private _organisationRoleId?: string;
  private _firstName?: string;
  private _familyName?: string;
  private _email?: string;
  private _phoneNumber?: string;
  private _emailVerified?: boolean;
  private _phoneNumberVerified?: boolean;
  private _displayPicture?: string;
  private _favouriteColour?: string;
  private _userEnabled?: boolean;
  private _userCreateDate?: Date;
  private _userLastModifiedDate?: Date;
  private _userStatus?: string;
  private _userLastLogin?: Date;
  private _mfaEnabled?: boolean;
  private _pictureBase64?: string | Observable<string>;
  private _runAnalytics: boolean;
  private _lockRunAnalytics: boolean;
  private _fullName: string;
  private _type: string;
  private _selected: boolean;
  private _role: RoleModel;
  private _roleId: string;
  private _oldRoleId: string;

  static returnNoUserModel(): OrganisationCognitoUser {
    const noUserModel = new OrganisationCognitoUser();
    noUserModel.userId = '';
    noUserModel.username = 'User Not Found';
    noUserModel.familyName = 'Missing';
    noUserModel.firstName = 'User';
    noUserModel.mfaEnabled = false;
    noUserModel.organisationId = environment.product_owner_uuid;
    noUserModel.organisationRoleId = 'No Role Assigned';
    noUserModel.phoneNumber = 'No Phone Number';
    noUserModel.phoneNumberVerified = false;
    noUserModel.userEnabled = false;
    noUserModel.userLastLogin = null;
    noUserModel.userLastModifiedDate = null;
    noUserModel.favouriteColour = '#2DD070';
    noUserModel.displayPicture = null;
    noUserModel.runAnalytics = false;
    noUserModel.lockRunAnalytics = false;
    return noUserModel;
  }
  constructor(
    userId?: string,
    organisationId?: string,
    organisationRoleId?: string,
    username?: string,
    firstName?: string,
    familyName?: string,
    email?: string,
    phoneNumber?: string,
    emailVerified?: boolean,
    phoneNumberVerified?: boolean,
    displayPicture?: string,
    favouriteColour?: string,
    userEnabled?: boolean,
    userCreateDate?: Date,
    userLastModifiedDate?: Date,
    userStatus?: string,
    userLastLogin?: Date,
    mfaEnabled?: boolean,
    pictureBase64?: string | Observable<string>,
    runAnalytics?: boolean,
    lockRunAnalytics?: boolean,
    fullName?: string,
    type?: string,
    selected?: boolean,
    role?: RoleModel,
    roleId?: string,
    oldRoleId?: string,
  ) {
    this._userId = userId;
    this._username = username;
    this._organisationId = organisationId;
    this._organisationRoleId = organisationRoleId;
    this._firstName = firstName;
    this._familyName = familyName;
    this._email = email;
    this._phoneNumber = phoneNumber;
    this._emailVerified = emailVerified;
    this._phoneNumberVerified = phoneNumberVerified;
    this._displayPicture = displayPicture;
    this._favouriteColour = favouriteColour;
    this._userEnabled = userEnabled;
    this._userCreateDate = userCreateDate;
    this._userLastModifiedDate = userLastModifiedDate;
    this._userStatus = userStatus;
    this._userLastLogin = userLastLogin;
    this._mfaEnabled = mfaEnabled;
    this._pictureBase64 = pictureBase64;
    this._runAnalytics = runAnalytics;
    this._lockRunAnalytics = lockRunAnalytics;
    this._fullName = fullName;
    this._type = type;
    this._selected = selected;
    this.role = role;
    this.roleId = roleId;
    this.oldRoleId = oldRoleId;
  }

  public get userId(): string {
    return this._userId;
  }

  public set userId(userId: string) {
    this._userId = userId;
  }

  public get organisationId(): string {
    return this._organisationId;
  }

  public set organisationId(organisationId: string) {
    this._organisationId = organisationId;
  }

  public get organisationRoleId(): string {
    return this._organisationRoleId;
  }
  public get username(): string {
    return this._username;
  }

  public set username(username: string) {
    this._username = username;
  }

  public set organisationRoleId(organisationRoleId: string) {
    this._organisationRoleId = organisationRoleId;
  }

  public get firstName(): string {
    return this._firstName;
  }

  public set firstName(firstName: string) {
    this._firstName = firstName;
  }

  public get familyName(): string {
    return this._familyName;
  }

  public set familyName(familyName: string) {
    this._familyName = familyName;
  }

  public get email(): string {
    return this._email;
  }

  public set email(email: string) {
    this._email = email;
  }

  public get phoneNumber(): string {
    return this._phoneNumber;
  }

  public set phoneNumber(phoneNumber: string) {
    this._phoneNumber = phoneNumber;
  }

  public get emailVerified(): boolean {
    return this._emailVerified;
  }

  public set emailVerified(emailVerified: boolean) {
    this._emailVerified = emailVerified;
  }

  public get phoneNumberVerified(): boolean {
    return this._phoneNumberVerified;
  }

  public set phoneNumberVerified(phoneNumberVerified: boolean) {
    this._phoneNumberVerified = phoneNumberVerified;
  }

  public get displayPicture(): string {
    return this._displayPicture;
  }

  public set displayPicture(displayPicture: string) {
    this._displayPicture = displayPicture;
  }

  public get favouriteColour(): string {
    return this._favouriteColour;
  }

  public set favouriteColour(favouriteColour: string) {
    this._favouriteColour = favouriteColour;
  }
  public get userEnabled(): boolean {
    return this._userEnabled;
  }

  public set userEnabled(userEnabled: boolean) {
    this._userEnabled = userEnabled;
  }

  public get userCreateDate(): Date {
    return this._userCreateDate;
  }

  public set userCreateDate(userCreateDate: Date) {
    this._userCreateDate = userCreateDate;
  }

  public get userLastModifiedDate(): Date {
    return this._userLastModifiedDate;
  }

  public set userLastModifiedDate(userLastModifiedDate: Date) {
    this._userLastModifiedDate = userLastModifiedDate;
  }

  public get userStatus(): string {
    return this._userStatus;
  }

  public set userStatus(userStatus: string) {
    this._userStatus = userStatus;
  }

  public get userLastLogin(): Date {
    return this._userLastLogin;
  }

  public set userLastLogin(userLastLogin: Date) {
    this._userLastLogin = userLastLogin;
  }

  public get mfaEnabled(): boolean {
    return this._mfaEnabled;
  }

  public set mfaEnabled(mfaEnabled: boolean) {
    this._mfaEnabled = mfaEnabled;
  }

  public get pictureBase64(): string | Observable<string> {
    return this._pictureBase64;
  }

  public set pictureBase64(pictureBase64: string | Observable<string>) {
    this._pictureBase64 = pictureBase64;
  }

  get runAnalytics(): boolean {
    return this._runAnalytics;
  }

  set runAnalytics(value: boolean) {
    this._runAnalytics = value;
  }

  get lockRunAnalytics(): boolean {
    return this._lockRunAnalytics;
  }

  set lockRunAnalytics(value: boolean) {
    this._lockRunAnalytics = value;
  }

  get fullName(): string {
    return this._fullName;
  }

  set fullName(value: string) {
    this._fullName = value;
  }

  get type(): string {
    return this._type;
  }

  set type(value: string) {
    this._type = value;
  }

  get selected(): boolean {
    return this._selected;
  }

  set selected(value: boolean) {
    this._selected = value;
  }

  get role(): RoleModel {
    return this._role;
  }

  set role(value: RoleModel) {
    this._role = value;
  }

  get roleId(): string {
    return this._roleId;
  }

  set roleId(value: string) {
    this._roleId = value;
  }

  get oldRoleId(): string {
    return this._oldRoleId;
  }

  set oldRoleId(value: string) {
    this._oldRoleId = value;
  }

  toJson(): Object {
    const object = {
      user_id: this.userId,
      organisation_id: this.organisationId,
      organisation_role_id: this.organisationRoleId,
      username: this.username,
      name: this.firstName,
      family_name: this.familyName,
      email: this.email,
      phone_number: this.phoneNumber,
      email_verified: this.emailVerified,
      phone_number_verified: this.phoneNumberVerified,
      display_picture: this.displayPicture,
      favourite_colour: this.favouriteColour,
      user_enabled: this.userEnabled,
      user_createDate: this.userCreateDate,
      user_last_modified_date: this.userLastModifiedDate,
      userS_status: this.userStatus,
      user_last_login: this.userLastLogin,
      mfa_enabled: this.mfaEnabled,
      picture_base64: this.pictureBase64,
      run_analytics: this.runAnalytics,
      lock_run_analytics: this.lockRunAnalytics,
      full_name: this.fullName,
      type: this.type,
      selected: this.selected,
      role: this.role,
      role_id: this.roleId,
      old_role_id: this.oldRoleId
    };
    Object.keys(object).forEach(
      (k) =>
        !object[k] && typeof object[k] !== 'boolean' && object[k] !== undefined && delete object[k]
    );
    return object;
  }

  fromJson(input: any): OrganisationCognitoUser {
    return new OrganisationCognitoUser(
      input['user_id'],
      input['organisation_id'],
      input['organisation_role_id'],
      input['username'],
      input['name'],
      input['family_name'],
      input['email'],
      input['phone_number'],
      input['email_verified'],
      input['phone_number_verified'],
      input['display_picture'],
      input['favourite_colour'],
      input['user_enabled'],
      input['user_createDate'],
      input['user_last_modified_date'],
      input['userS_status'],
      input['user_last_login'],
      input['mfa_enabled'],
      input['picture_base64'],
      input['run_analytics'],
      input['lock_run_analytics'],
      input['type'],
      input['selected'],
      input['role'],
      input['role_id'],
      input['old_role_id']
    );
  }
}
